<template>
  <v-card class="project-card" width="186" @click="projectClick(project.id)">
    <div class="project-card__id">#{{ project.id }}</div>
    <v-img
      v-if="bannerImageUrl"
      :src="bannerImageUrl"
      height="96"
      gradient="to top, rgba(18,18,18,0.94) 4%, rgba(18,18,18,0.35) 52%, rgba(18,18,18,0.05) 100%"
    >
      <div class="project-card__overlay">
        <div class="project-card__title">{{ project.name }}</div>
      </div>
    </v-img>

    <div
      v-else
      class="project-card__overlay project-card__placeholder"
      :style="placeholderStyle"
    >
      <span class="project-card__monogram">{{ initial }}</span>
      <div class="project-card__title">{{ project.name }}</div>
    </div>

    <v-card-text
      v-if="descriptionText"
      class="project-card__desc"
      v-html="descriptionText"
    ></v-card-text>

    <v-divider class="project-card__divider"></v-divider>

    <v-card-actions class="project-card__actions">
      <span class="project-card__cta">Explore</span>
      <v-icon size="15" class="project-card__cta-icon">mdi-arrow-right</v-icon>
    </v-card-actions>
  </v-card>
</template>

<script>
const fileDownload = require('js-file-download')
import ProjectsService from '@/services/ProjectsService'
import MasterplanService from '@/services/MasterplanService'
import PoiService from '@/services/PoiService'
import MediaService from '@/services/MediaService'
import Enums from '../helpers/Enums'
export default {
  props: {
    project: Object
  },
  computed: {
    initial() {
      return (this.project.name || '?')
        .trim()
        .charAt(0)
        .toUpperCase()
    },
    placeholderStyle() {
      // Deterministic, cohesive dark gradient per project so cards stay
      // distinguishable even without a title image.
      const hue = ((this.project.id || 0) * 47) % 360
      return {
        background: `linear-gradient(135deg, hsl(${hue}, 30%, 17%), hsl(${(hue +
          42) %
          360}, 36%, 9%))`
      }
    }
  },
  methods: {
    getMediaContent: function(project) {
      if (!project || !Array.isArray(project.Media)) return
      const description = project.Media.find(
        m => m.mediaTypeId === Enums.MediaType.project_text_description
      )
      if (description && description.textualContent) {
        this.descriptionText = description.textualContent.replace(
          /(\\r)*\\n/g,
          '<br>'
        )
      }
    },
    // Walk the project hierarchy (masterplans -> masterplan POI -> DZI media)
    // and use the first masterplan deep-zoom thumbnail we can find as the
    // card picture. Best-effort: falls back to the gradient placeholder.
    resolveMasterplanDzi: async function(projectId) {
      try {
        const masterplans =
          (await ProjectsService.getProjectMasterplans(projectId)).data || []
        for (const masterplan of masterplans) {
          const pois =
            (await MasterplanService.getPoisForMasterplan(masterplan.id))
              .data || []
          const masterplanPoi = pois.find(
            p => p.poiTypeId === Enums.PoiType.poi_type_masterplan
          )
          if (!masterplanPoi) continue

          const media =
            (await PoiService.getMediaForPoi(masterplanPoi.id)).data || []
          const dzi = media.find(
            m => m.path && m.path.toLowerCase().indexOf('.dzi') !== -1
          )
          if (!dzi) continue

          const resp = await MediaService.getMediaFile(
            `/pois/${masterplanPoi.id}/media-content/${
              dzi.id
            }/file?type=thumbnail`
          )
          if (resp && resp.data) {
            const mimeType = resp.headers['content-type'].toLowerCase()
            const imgBase64 = new Buffer(resp.data, 'binary').toString('base64')
            this.bannerImageUrl = `data:${mimeType};base64,${imgBase64}`
            return
          }
        }
      } catch (e) {
        // leave the gradient placeholder in place
      }
    },
    projectClick: async function(projectId) {
      await this.$store.dispatch('BreadcrumbsStore/clear')
      this.$router.push('/projects/' + projectId).catch(err => {})
    },
    projectExport: async function(projectId) {
      const response = await ProjectsService.getExportedProject({
        id: projectId,
        name: 'export.json'
      })

      fileDownload(JSON.stringify(response), 'export.json')
    }
  },
  data: function() {
    return {
      bannerImageUrl: '',
      descriptionText: ''
    }
  },
  created: function() {
    this.getMediaContent(this.$props.project)
    this.resolveMasterplanDzi(this.$props.project.id)
  }
}
</script>

<style scoped>
.project-card {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  background: #1c1c1c;
  border: 1px solid rgba(255, 255, 255, 0.06);
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease,
    border-color 0.18s ease;
}
.project-card__id {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
  padding: 1px 7px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.5;
  letter-spacing: 0.02em;
  color: #fff;
  background: rgba(0, 0, 0, 0.55);
  border-radius: 999px;
  backdrop-filter: blur(2px);
}
.project-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 26px rgba(0, 0, 0, 0.45);
  border-color: rgba(255, 152, 0, 0.55);
}
.project-card__overlay {
  display: flex;
  align-items: flex-end;
  height: 100%;
  width: 100%;
}
.project-card__placeholder {
  position: relative;
  height: 96px;
}
.project-card__monogram {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -62%);
  font-size: 34px;
  font-weight: 700;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.14);
}
.project-card__title {
  width: 100%;
  padding: 6px 11px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.25;
  color: #fff;
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.65);
}
.project-card__desc {
  font-size: 11.5px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.62);
  max-height: 43px;
  overflow: hidden;
  padding: 8px 11px 4px;
}
.project-card__divider {
  opacity: 0.5;
}
.project-card__actions {
  padding: 6px 11px;
  display: flex;
  align-items: center;
  gap: 4px;
}
.project-card__cta {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #ff9800;
}
.project-card__cta-icon {
  color: #ff9800 !important;
}
</style>
